import { useMediaQuery, useTheme } from '@mui/material';
import React, { useRef, useState } from 'react';

import { Dialog } from '@/components/common/Dialog';

export function SourceToggle({
  source,
  onChange,
  marginTop = 16,
}: {
  source: 'seller' | 'buyer';
  onChange: (s: 'seller' | 'buyer') => void;
  marginTop?: number;
}) {
  return (
    <div style={{ marginTop }}>
      <div style={{ display: 'flex', gap: 8 }}>
        {(['seller', 'buyer'] as const).map((opt) => {
          const active = source === opt;
          return (
            <button
              key={opt}
              onClick={() => onChange(opt)}
              style={{
                flex: 1,
                height: 36,
                border: 0,
                borderRadius: 8,
                background: active ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
                color: active ? 'var(--tx-text)' : C.subtle,
                fontFamily: font,
                fontSize: 13,
                fontWeight: active ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 0.15s',
                letterSpacing: '-0.02em',
              }}>
              {opt === 'seller' ? 'Seller' : 'Buyer'}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function UploadReportInline({
  property,
  onUpload,
}: {
  property: UploadReportProperty;
  onUpload: (propertyId: string, file: File, source: 'seller' | 'buyer') => Promise<void>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [source, setSource] = useState<'seller' | 'buyer'>('seller');
  const fileRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSubmit = async () => {
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(property.id, file, source);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      style={{
        background: 'var(--tx-glass-panel)',
        backgroundImage: 'var(--tx-surface-light)',
        boxShadow: 'var(--tx-shadow)',
        borderRadius: '24px',
        width: 480,
        padding: 25,
      }}>
      <div style={{ marginBottom: 6 }}>
        <div style={{ fontSize: 17, fontWeight: 600, color: C.text, fontFamily: font }}>
          Upload report
        </div>
        <div style={{ fontSize: 13, color: C.subtle, fontFamily: font, marginTop: 2 }}>
          {property.address}
          {property.city && property.state ? ` · ${property.city}, ${property.state}` : ''}
        </div>
      </div>

      {property.hasReport && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'var(--tx-fill)',
            border: 0,
            borderRadius: 10,
            padding: '10px 14px',
            margin: '16px 0',
          }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke={L.resolved}
            strokeWidth="2"
            strokeLinecap="round">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: L.resolved, fontFamily: font }}>
              {property.inspectionReportCount} document
              {property.inspectionReportCount !== 1 ? 's' : ''} on file
            </div>
          </div>
        </div>
      )}

      {file ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '10px 14px',
            background: 'var(--tx-fill)',
            borderRadius: 10,
            border: 0,
            marginTop: 16,
          }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#bbb7af"
            strokeWidth="2"
            strokeLinecap="round"
            style={{ flexShrink: 0, marginTop: 2 }}>
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--tx-text)',
                fontFamily: font,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {file.name}
            </div>
            <div style={{ fontSize: 11, color: '#737373', fontFamily: font }}>
              {(file.size / 1024 / 1024).toFixed(2)} MB
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              {(['seller', 'buyer'] as const).map((opt) => {
                const active = source === opt;
                return (
                  <button
                    key={opt}
                    onClick={() => setSource(opt)}
                    style={{
                      fontSize: 11,
                      fontFamily: font,
                      padding: '3px 10px',
                      borderRadius: 6,
                      border: 0,
                      background: active ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
                      color: active ? 'var(--tx-text)' : 'var(--tx-faint)',
                      fontWeight: active ? 600 : 400,
                      cursor: 'pointer',
                      letterSpacing: '-0.01em',
                      transition: 'all 0.15s',
                    }}>
                    {opt === 'seller' ? 'Seller' : 'Buyer'}
                  </button>
                );
              })}
            </div>
          </div>
          <button
            onClick={() => setFile(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--tx-faint)',
              display: 'flex',
              alignItems: 'center',
              padding: 0,
              flexShrink: 0,
            }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      ) : (
        <div
          onClick={() => fileRef.current?.click()}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const f = e.dataTransfer.files[0];
            if (f) setFile(f);
            e.currentTarget.style.borderColor = C.border;
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.style.borderColor = 'rgba(192,255,2,0.5)';
            e.currentTarget.style.backgroundColor = 'rgba(192,255,2,0.04)';
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = C.border;
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          style={{
            border: `1.5px dashed ${C.border}`,
            borderRadius: '12px',
            padding: '24px 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            marginTop: 16,
          }}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke={C.subtle}
            strokeWidth="1.5"
            strokeLinecap="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <div style={{ fontSize: 14, color: '#737373', fontFamily: font }}>
            {isMobile ? 'Click to upload' : 'Drag & drop or click to upload'}
          </div>
          <div style={{ fontSize: 11, color: '#555', fontFamily: font }}>
            PDF, DOCX, JPG, or PNG (max 25MB)
          </div>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept=".pdf,.docx,.jpg,.jpeg,.png"
        style={{ display: 'none' }}
        onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
        <button
          onClick={handleSubmit}
          disabled={!file || uploading}
          style={{
            ...btnPrimary,
            opacity: file && !uploading ? 1 : 0.4,
            cursor: file && !uploading ? 'pointer' : 'not-allowed',
          }}>
          {uploading ? 'Uploading…' : 'Upload report'}
        </button>
      </div>
    </div>
  );
}

const btnPrimary: React.CSSProperties = {
  background: 'var(--tx-fill-2)',
  color: 'var(--tx-text)',
  border: 'none',
  borderRadius: 10,
  height: 40,
  padding: '0 24px',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: "'Manrope', sans-serif",
  letterSpacing: '-0.02em',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const btnGhost: React.CSSProperties = {
  background: 'var(--tx-fill)',
  color: 'var(--tx-muted)',
  border: 0,
  borderRadius: 10,
  height: 40,
  padding: '0 24px',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: "'Manrope', sans-serif",
  letterSpacing: '-0.02em',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const C = {
  border: 'var(--tx-line)',
  text: 'var(--tx-text)',
  muted: 'var(--tx-muted)',
  subtle: 'var(--tx-faint)',
};
const L = { resolved: '#C0FF02' };
const font = "'Manrope', sans-serif";

export interface UploadReportProperty {
  id: string;
  address: string;
  city?: string;
  state?: string;
  hasReport?: boolean;
  inspectionReportCount?: number;
}

export interface UploadFile {
  file: File;
  source: 'seller' | 'buyer';
}

export function UploadReportModal({
  property,
  onClose,
  onUpload,
}: {
  property: UploadReportProperty;
  onClose: () => void;
  onUpload: (propertyId: string, files: UploadFile[]) => Promise<void>;
}) {
  return <MultiFileUploadReportModal property={property} onClose={onClose} onUpload={onUpload} />;
}

function MultiFileUploadReportModal({
  property,
  onClose,
  onUpload,
}: {
  property: UploadReportProperty;
  onClose: () => void;
  onUpload: (propertyId: string, files: UploadFile[]) => Promise<void>;
}) {
  const MAX_FILES = 20;
  const MAX_FILE_SIZE = 25 * 1024 * 1024;
  const MAX_TOTAL_SIZE = 150 * 1024 * 1024;
  const ALLOWED_MIME_TYPES = new Set([
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
  ]);
  const defaultSource: UploadFile['source'] = 'seller';
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const addFiles = (incoming: FileList | File[]) => {
    const additions = Array.from(incoming);
    setFiles((current) => {
      const known = new Set(
        current.map(({ file }) => `${file.name}:${file.size}:${file.lastModified}`),
      );
      const unique = additions.filter(
        (file) => !known.has(`${file.name}:${file.size}:${file.lastModified}`),
      );
      const valid = unique.filter(
        (file) => ALLOWED_MIME_TYPES.has(file.type) && file.size > 0 && file.size <= MAX_FILE_SIZE,
      );
      const availableSlots = Math.max(0, MAX_FILES - current.length);
      const remainingSize = MAX_TOTAL_SIZE - current.reduce((total, item) => total + item.file.size, 0);
      const accepted: File[] = [];
      for (const file of valid) {
        if (accepted.length >= availableSlots || file.size > remainingSize - accepted.reduce((total, item) => total + item.size, 0)) break;
        accepted.push(file);
      }
      const rejected = unique.length - accepted.length;
      setSelectionError(
        rejected
          ? 'PDF, DOCX, JPG, or PNG only · 25 MB each · 20 files / 150 MB per upload.'
          : null,
      );
      return [...current, ...accepted.map((file) => ({ file, source: defaultSource }))];
    });
  };

  const updateSource = (index: number, source: UploadFile['source']) => {
    setFiles((current) =>
      current.map((item, itemIndex) => (itemIndex === index ? { ...item, source } : item)),
    );
  };

  const submit = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setUploadError(null);
    try {
      await onUpload(property.id, files);
      onClose();
    } catch (error) {
      setUploadError(
        error instanceof Error && error.message
          ? error.message
          : 'Unable to upload these documents. Please try again.',
      );
    } finally {
      setUploading(false);
    }
  };

  const openFilePicker = () => fileRef.current?.click();

  return (
    <Dialog
      open={true}
      onClose={uploading ? undefined : onClose}
      disableClose={uploading}
      disableBackdropClick={uploading}
      width={675}
      containerProps={{ gap: 0, p: 0 }}>
      <div style={{ padding: 25 }}>
        <div>
          <div
            style={{
              fontSize: 17,
              fontWeight: 600,
              color: C.text,
              fontFamily: font,
            }}>
            Upload report
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginTop: 4,
            }}>
            <div
              style={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 13,
                color: C.subtle,
                fontFamily: font,
              }}>
              {property.address}
              {property.city && property.state ? ` · ${property.city}, ${property.state}` : ''}
            </div>
            {property.hasReport && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round">
                  <path d="M8 2h7l4 4v14a2 2 0 01-2 2H8a2 2 0 01-2-2V4a2 2 0 012-2z" />
                  <polyline points="15 2 15 6 19 6" />
                  <path d="M4 6v14a2 2 0 002 2h1" />
                </svg>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.muted, fontFamily: font }}>
                  {property.inspectionReportCount} document
                  {property.inspectionReportCount !== 1 ? 's' : ''} on file
                </span>
              </div>
            )}
          </div>
        </div>

        <div
          onDrop={(event) => {
            event.preventDefault();
            setIsDragging(false);
            if (!uploading) addFiles(event.dataTransfer.files);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            if (!uploading) setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          style={{ marginTop: 18 }}>
          {files.length > 0 ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 10,
                }}>
                <span
                  style={{
                    color: C.muted,
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    fontFamily: font,
                  }}>
                  {files.length} FILE{files.length === 1 ? '' : 'S'} READY
                </span>
                <button
                  onClick={openFilePicker}
                  disabled={uploading}
                  style={{ ...btnGhost, height: 30, padding: '0 12px', fontSize: 11 }}>
                  + Add files
                </button>
              </div>
              <div
                style={{
                  maxHeight: 350,
                  overflowY: 'auto',
                  paddingRight: 2,
                  borderRadius: 14,
                  background: isDragging ? 'var(--tx-fill)' : 'transparent',
                  transition: 'background 0.15s ease',
                }}>
                {files.map(({ file, source }, index) => (
                  <div
                    key={`${file.name}:${file.size}:${file.lastModified}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 14px',
                      border: 'none',
                      borderRadius: 10,
                      background: 'var(--tx-fill)',
                      marginBottom: index === files.length - 1 ? 0 : 8,
                    }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          minWidth: 0,
                          fontSize: 13,
                          fontWeight: 600,
                          color: C.text,
                          fontFamily: font,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                        {file.name}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: C.subtle,
                          fontFamily: font,
                          marginTop: 2,
                        }}>
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      {(['seller', 'buyer'] as const).map((option) => {
                        const active = source === option;
                        return (
                          <button
                            key={option}
                            onClick={() => updateSource(index, option)}
                            disabled={uploading}
                            style={{
                              minWidth: 62,
                              height: 26,
                              padding: '0 8px',
                              borderRadius: 8,
                              border: 0,
                              background: active ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
                              color: active ? C.text : C.subtle,
                              fontFamily: font,
                              fontSize: 11,
                              fontWeight: active ? 600 : 500,
                              cursor: uploading ? 'default' : 'pointer',
                            }}>
                            {option === 'seller' ? 'Seller' : 'Buyer'}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      aria-label={`Remove ${file.name}`}
                      onClick={() =>
                        setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))
                      }
                      disabled={uploading}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: uploading ? 'default' : 'pointer',
                        color: C.subtle,
                        display: 'flex',
                        padding: 4,
                      }}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <button
              onClick={openFilePicker}
              style={{
                width: '100%',
                border: `1.5px dashed ${C.border}`,
                borderRadius: '12px',
                padding: '24px 16px',
                background: 'transparent',
                color: C.subtle,
                cursor: 'pointer',
                fontFamily: font,
              }}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 00-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <div style={{ fontSize: 14, marginTop: 8 }}>Drag & drop files or click to select</div>
              <div style={{ fontSize: 12, color: '#555', marginTop: 6 }}>
                PDF, DOCX, JPG, or PNG (max 25MB)
              </div>
            </button>
          )}
        </div>

          {selectionError && (
            <div style={{ marginTop: 10, color: '#ff9b50', fontSize: 11, fontFamily: font }}>
              {selectionError}
            </div>
          )}
          {uploadError && (
            <div role="alert" style={{ marginTop: 10, color: '#ff9b50', fontSize: 12, fontFamily: font }}>
              {uploadError}
            </div>
          )}

          <input
            ref={fileRef}
            type="file"
            multiple
            accept=".pdf,.docx,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
          onChange={(event) => {
            if (event.target.files) addFiles(event.target.files);
            event.currentTarget.value = '';
          }}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button onClick={onClose} disabled={uploading} style={btnGhost}>
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={files.length === 0 || uploading}
            style={{
              ...btnPrimary,
              opacity: files.length > 0 && !uploading ? 1 : 0.4,
              cursor: files.length > 0 && !uploading ? 'pointer' : 'not-allowed',
            }}>
            {uploading
              ? `Uploading ${files.length}…`
              : `Upload ${files.length === 1 ? 'report' : `all (${files.length})`}`}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
